import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Mail, Phone, Send, UserRound } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { guideService } from "../services/guideService";
import { loadGuideHistory, saveGuideHistory, type StoredGuideMessage } from "../services/guideHistoryStorage";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { toast } from "sonner";
import { GuideAssistantText, GuideTypingIndicator } from "../components/GuideAssistantText";
import { GuideAssetImage } from "../components/GuideAssetImage";
import type { GuideAction } from "../api/guide";
import { formatApiError } from "../api/client";

type ChatMessage = StoredGuideMessage;

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function GuidePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId, setNavbarVisible, user } = useAppStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [quickQuestions, setQuickQuestions] = useState<string[]>([]);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingMsgId, setTypingMsgId] = useState<string | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const blurTimer = useRef<number | null>(null);

  const lang = i18n.language.startsWith("en") ? "en" : "ru";

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (list) {
        list.scrollTo({ top: list.scrollHeight, behavior });
        return;
      }
      bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    });
  }, []);

  useEffect(() => {
    setNavbarVisible(false);
    return () => {
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
      setNavbarVisible(true);
    };
  }, [setNavbarVisible]);

  useEffect(() => {
    if (!selectedEventId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const saved = loadGuideHistory(user.id, selectedEventId, lang);
    (async () => {
      setLoading(true);
      try {
        const data = await guideService.getWelcome(selectedEventId, lang);
        if (cancelled) return;
        setQuickQuestions(data.suggestions);
        setLlmEnabled(data.llmEnabled);
        if (saved.length > 0) {
          setMessages(saved);
        } else {
          setMessages([
            {
              id: newId(),
              role: "assistant",
              content: data.welcome,
            },
          ]);
        }
      } catch (err) {
        if (!cancelled) {
          if (saved.length > 0) {
            setMessages(saved);
          }
          toast.error(err instanceof Error ? err.message : t("visitor.loading_error"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, lang, t, user.id]);

  useEffect(() => {
    if (!selectedEventId || loading || messages.length === 0) return;
    saveGuideHistory(user.id, selectedEventId, lang, messages);
  }, [messages, selectedEventId, lang, loading, user.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, keyboardInset, scrollToBottom]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset > 40 ? inset : 0);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("focusin", update);
    window.addEventListener("focusout", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("focusin", update);
      window.removeEventListener("focusout", update);
    };
  }, []);

  const onInputFocus = () => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    setNavbarVisible(false);
    window.setTimeout(() => scrollToBottom("smooth"), 80);
    window.setTimeout(() => scrollToBottom("smooth"), 320);
  };

  const onInputBlur = () => {
    blurTimer.current = window.setTimeout(() => setNavbarVisible(true), 180);
  };

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || !selectedEventId || sending) return;

    maxPlatform.hapticFeedback("light");
    const userMsg: ChatMessage = { id: newId(), role: "user", content: message };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    const history = [...messages, userMsg]
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const data = await guideService.send(selectedEventId, message, history, lang);
      const assistantId = newId();
      setTypingMsgId(assistantId);
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: "assistant",
          content: data.reply,
          actions: data.actions,
          source: data.source,
        },
      ]);
    } catch (err) {
      toast.error(formatApiError(err, t("guide.error_send")));
      setMessages((prev) => prev.slice(0, -1));
      setInput(message);
    } finally {
      setSending(false);
    }
  };

  const handleNavigate = (path: string) => {
    maxPlatform.hapticFeedback("medium");
    navigate(path);
  };

  const handleCall = (phone: string) => {
    maxPlatform.hapticFeedback("medium");
    window.location.href = `tel:${phone.replace(/\s/g, "")}`;
  };

  const handleEmail = (email: string) => {
    maxPlatform.hapticFeedback("medium");
    window.location.href = `mailto:${email}`;
  };

  const handleEscalate = async () => {
    if (!selectedEventId || sending) return;
    maxPlatform.hapticFeedback("light");
    setSending(true);
    try {
      const data = await guideService.escalate(selectedEventId, lang);
      const assistantId = newId();
      setTypingMsgId(assistantId);
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: "assistant",
          content: data.reply,
          actions: data.actions,
          source: data.source,
        },
      ]);
    } catch (err) {
      toast.error(formatApiError(err, t("guide.error_send")));
    } finally {
      setSending(false);
    }
  };

  const handleAction = (action: GuideAction) => {
    if (action.type === "navigate") handleNavigate(action.path);
    else if (action.type === "call") handleCall(action.phone);
    else if (action.type === "email") handleEmail(action.email);
    else if (action.type === "open_url") {
      try {
        window.open(action.url, "_blank", "noopener,noreferrer");
      } catch {
        /* ignore */
      }
    } else if (action.type === "show_image") {
      /* rendered inline */
    } else void handleEscalate();
  };

  const actionKey = (msgId: string, action: GuideAction) => {
    if (action.type === "navigate") return `${msgId}-nav-${action.path}`;
    if (action.type === "call") return `${msgId}-call-${action.phone}`;
    if (action.type === "email") return `${msgId}-email-${action.email}`;
    if (action.type === "open_url") return `${msgId}-url-${action.url}`;
    if (action.type === "show_image") return `${msgId}-img-${action.url}`;
    return `${msgId}-escalate`;
  };

  // /guide is always the exhibition guide (visitor_guide settings) — never tech support.
  const headerTitle = t("guide.title");
  const headerSubtitle = llmEnabled ? t("guide.subtitle_ai") : t("guide.subtitle_offline");

  return (
    <div
      className="flex flex-col flex-1 min-h-0 h-full overflow-hidden"
      style={{ paddingBottom: keyboardInset > 0 ? keyboardInset : undefined }}
    >
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        back
        backTo="/home"
      />

      <PageShell noBottomPad noPadding className="flex flex-col flex-1 min-h-0 !pb-0 overflow-hidden">
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto overscroll-contain pt-2 pb-3 px-3 space-y-3 min-h-0"
        >
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-indigo-500" size={28} />
            </div>
          ) : (
            messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed ${
                    msg.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-md"
                      : "bg-white/80 border border-white/60 text-gray-800 rounded-bl-md shadow-sm"
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">
                    {msg.role === "user" ? (
                      msg.content
                    ) : (
                      <GuideAssistantText
                        text={msg.content}
                        animate={msg.id === typingMsgId}
                        onComplete={() => setTypingMsgId(null)}
                      />
                    )}
                  </div>
                  {msg.role === "assistant" && msg.source === "fallback" && (
                    <p className="mt-1.5 text-[11px] text-amber-700/80">{t("guide.fallback_hint")}</p>
                  )}
                  {msg.role === "assistant" &&
                    msg.actions?.some((a) => a.type === "show_image") &&
                    msg.id !== typingMsgId && (
                      <div className="mt-2 space-y-2">
                        {msg.actions
                          .filter((a): a is Extract<GuideAction, { type: "show_image" }> => a.type === "show_image")
                          .map((a) => (
                            <GuideAssetImage key={a.url} url={a.url} label={a.label} />
                          ))}
                      </div>
                    )}
                  {msg.actions &&
                    msg.actions.filter((a) => a.type !== "show_image").length > 0 &&
                    msg.id !== typingMsgId && (
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {msg.actions
                        .filter((a) => a.type !== "show_image")
                        .map((a) => (
                        <button
                          key={actionKey(msg.id, a)}
                          type="button"
                          onClick={() => handleAction(a)}
                          className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-1.5 rounded-full shadow-sm active:opacity-90 ${
                            a.type === "call"
                              ? "bg-emerald-600 text-white"
                              : a.type === "email"
                                ? "bg-sky-600 text-white"
                                : a.type === "escalate"
                                  ? "bg-amber-500 text-white"
                                  : a.type === "open_url"
                                    ? "bg-violet-600 text-white"
                                    : "bg-indigo-600 text-white active:bg-indigo-700"
                          }`}
                        >
                          {a.type === "call" ? <Phone size={14} /> : null}
                          {a.type === "email" ? <Mail size={14} /> : null}
                          {a.type === "escalate" ? <UserRound size={14} /> : null}
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <GuideTypingIndicator label={t("visitor.typing")} />
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {!loading && quickQuestions.length > 0 && (
          <div className="shrink-0 px-3 pt-2 pb-1 border-t border-white/30 bg-white/50 backdrop-blur-md">
            <p className="text-[11px] font-medium text-gray-500 mb-1.5 px-0.5">
              {t("guide.quick_questions")}
            </p>
            <div
              className="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5"
              style={{ scrollbarWidth: "none" }}
            >
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  type="button"
                  disabled={sending}
                  onClick={() => void send(q)}
                  className="shrink-0 max-w-[85vw] text-left text-[12px] leading-snug px-3 py-2 rounded-2xl bg-white border border-indigo-100 text-indigo-900 shadow-sm active:bg-indigo-50 disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="shrink-0 px-2 sm:px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-white/20 bg-white/50 backdrop-blur-md">
          <form
            className="flex items-end gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              placeholder={t("guide.input_placeholder")}
              rows={2}
              className="flex-1 min-h-[52px] max-h-36 resize-none rounded-xl border border-gray-200/90 bg-white px-3.5 py-3 text-[15px] leading-snug outline-none focus:ring-2 focus:ring-indigo-300/60"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send(input);
                }
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              aria-label={t("guide.send")}
              className="shrink-0 h-9 w-9 mb-0.5 rounded-full bg-indigo-600 text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>
      </PageShell>
    </div>
  );
}
